<template>
  <div class="tinto-page">
    <Loading :isActive="loading" />

    <TintometricoVincularEanModal
      name="baseForm"
      context-title="Base em edição"
      :context-fields="baseContextFields"
      :ean-atual="editForm.ean"
      :endpoint="`/admin/tintometrico/base/${editForm.id}`"
      @saved="load"
    />

    <!-- ===== FILTERS ===== -->
    <div class="tinto-filters">
      <CRow>
        <CCol md="5">
          <input class="form-control" v-model="filtros.termo" placeholder="Buscar por nome, código ou EAN..." @keyup.enter="load()" />
        </CCol>
        <CCol md="3">
          <select class="form-control" v-model="filtros.fabricanteId" @change="load()">
            <option value="">Todos os fabricantes</option>
            <option v-for="(n,id) in fabricantes" :key="id" :value="id">{{ n }}</option>
          </select>
        </CCol>
        <CCol md="2">
          <input class="form-control" v-model="filtros.codigo" placeholder="Base (A2, P...)" @keyup.enter="load()" />
        </CCol>
        <CCol md="2">
          <button class="button button-primary" @click="load()">Buscar</button>
        </CCol>
      </CRow>
    </div>

    <!-- ===== TABLE ===== -->
    <div class="tinto-table-wrap">
      <table class="tinto-table">
        <thead><tr>
          <th style="width:50px">ID</th>
          <th style="width:280px" class="tinto-sortable" :class="{'is-active': sort.by==='nome'}" @click="sortBy('nome')">
            Linha<fa-icon :icon="sort.by==='nome' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:90px" class="tinto-sortable" :class="{'is-active': sort.by==='codigo'}" @click="sortBy('codigo')">
            Código<fa-icon :icon="sort.by==='codigo' ? (sort.dir==='asc' ? 'arrow-up' : 'arrow-down') : 'arrow-down'" class="sort-icon" />
          </th>
          <th style="width:140px">Embalagem</th>
          <th style="width:130px">EAN</th>
          <th style="width:110px">Fabricante</th>
          <th style="width:50px"></th>
        </tr></thead>
        <tbody>
          <tr v-if="items.length === 0"><td colspan="7" class="tinto-table__empty">Nenhuma base encontrada</td></tr>
          <tr v-for="r in items" :key="r.id">
            <td>{{ r.id }}</td>
            <td class="tinto-table__primary">
              <span v-if="r.linhaNome">{{ r.linhaNome }}</span>
              <span v-else class="tinto-table__muted">—</span>
            </td>
            <td>{{ r.codigo }}</td>
            <td>
              <span v-if="r.embalagemNome">{{ r.embalagemNome }}</span>
              <span v-if="r.embalagemVolumeMl" class="tinto-table__sub" style="display:inline;margin-left:4px">({{ formatVolume(r.embalagemVolumeMl) }})</span>
              <span v-if="!r.embalagemNome" class="tinto-table__muted">—</span>
            </td>
            <td><span v-if="r.ean">{{ r.ean }}</span><span v-else class="tinto-table__muted">—</span></td>
            <td>{{ r.nomeFabricante }}</td>
            <td>
              <button class="button button-primary" @click="openEdit(r)" title="Gerenciar EAN">
                <fa-icon icon="pencil-alt" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="tinto-pagination" v-if="paginator.last_page > 1">
      <pagination :data="paginator" :limit="5" @pagination-change-page="load" />
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import TintometricoVincularEanModal from "./TintometricoVincularEanModal.vue";
import listagemMixin from "./listagemMixin";

export default {
  name: "tintometrico-base",
  components: { Loading, TintometricoVincularEanModal },
  mixins: [listagemMixin],
  props: ['fabricantes'],

  data() {
    return {
      listagemEndpoint: '/admin/tintometrico/base',
      sort: { by: 'codigo', dir: 'asc' },
      filtros: { termo: '', fabricanteId: '', codigo: '' },
      editForm: { id: null, codigo: '', linhaNome: '', embalagemNome: '', embalagemVolumeMl: null, nomeFabricante: '', ean: '' },
    };
  },

  computed: {
    embalagemFormatada() {
      const { embalagemNome, embalagemVolumeMl } = this.editForm;
      if (!embalagemNome && !embalagemVolumeMl) return '';
      const volume = embalagemVolumeMl ? this.formatVolume(embalagemVolumeMl) : '';
      if (embalagemNome && volume) return `${embalagemNome} (${volume})`;
      return embalagemNome || volume;
    },

    baseContextFields() {
      return [
        { label: 'ID',          value: this.editForm.id ? '#' + this.editForm.id : '—', badge: 'secondary', size: 'col-md-3' },
        { label: 'Código',      value: this.editForm.codigo || '—',         badge: 'info',      size: 'col-md-3' },
        { label: 'Linha',       value: this.editForm.linhaNome || '—',                          size: 'col-md-6' },
        { label: 'Embalagem',   value: this.embalagemFormatada || '—',                          size: 'col-md-6' },
        { label: 'Fabricante',  value: this.editForm.nomeFabricante || '—',                     size: 'col-md-6' },
      ];
    },
  },

  methods: {
    appendFiltros(params) {
      if (this.filtros.fabricanteId) params.append('fabricante_id', this.filtros.fabricanteId);
      const searchTerm = this.filtros.codigo || this.filtros.termo;
      if (searchTerm) params.append('q', searchTerm);
    },

    openEdit(base) {
      this.editForm = {
        id: base.id,
        codigo: base.codigo,
        linhaNome: base.linhaNome,
        embalagemNome: base.embalagemNome,
        embalagemVolumeMl: base.embalagemVolumeMl,
        nomeFabricante: base.nomeFabricante,
        ean: base.ean,
      };
      this.$modal.show('baseForm');
    },

    formatVolume(ml) {
      return ml >= 1000 ? (ml / 1000).toFixed(1) + 'L' : ml + 'ml';
    },
  },
};
</script>
